import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { LiliButton } from '@lili/lili-frontend-common';
import { ReactComponent as SecurityIcon } from '../../../../../assets/icons/svg/shared/ic-shared-security.svg';
import { useRequestedVendorDetailsSubmit } from '../../../hooks/useGetRequestedVendorDetails';
import { IServerErrorResponse } from '../../../../../types';
import { flattenServerErrorResponse } from '../../../../../helpers/errors.helper';
import { LiliTextInput } from '../../../../../DesignSystem/LiliTextInput/LiliTextInput';

interface VendorDetailsRequestFormInputProps {
  businessName: string;
  onError: (error: IServerErrorResponse) => void;
  onSuccess: () => void;
}

export const VendorDetailsRequestFormInput = ({
  businessName,
  onSuccess,
  onError,
}: VendorDetailsRequestFormInputProps) => {
  const [searchParams] = useSearchParams();

  const [accountNumberInput, setAccountNumberInput] = useState<string>('');
  const [routingNumberInput, setRoutingNumberInput] = useState<string>('');
  const [accountingNumberErrorMessage, setAccountingNumberErrorMessage] = useState<string | undefined>();
  const [routingNumberErrorMessage, setRoutingNumberErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation();

  const onHandleError = (error: IServerErrorResponse) => {
    const flattenError = flattenServerErrorResponse(error);
    switch (flattenError.code) {
      case 5044: {
        setRoutingNumberErrorMessage(t('ASK_VENDOR.FORM.INLINE_ERRORS.ACCOUNT_NUMBER'));
        break;
      }
      case 50672: {
        setRoutingNumberErrorMessage(t('ASK_VENDOR.FORM.INLINE_ERRORS.ROUTING_NUMBER'));
        break;
      }
      case 50673: {
        setAccountingNumberErrorMessage(t('ASK_VENDOR.FORM.INLINE_ERRORS.ROUTING_NUMBER'));
        break;
      }
      default: {
        onError(error);
      }
    }
  };
  const { mutateAsync: savePaymentDetails, isLoading } = useRequestedVendorDetailsSubmit(
    (error: IServerErrorResponse) => {
      mixpanel.track('View Ask Vendor - Vendor view Success Screen', {
        'Flow end screen status': 'Failed',
      });
      onHandleError(error);
    },
  );
  const onSubmit = (accountingNumber: string, routingNumber: string) => {
    mixpanel.track('Act on Ask Vendor - Vendor view Payment Details Screen', {
      Action: 'Confirm',
    });

    const cleanedAccountingNumber = accountingNumber.replace(/\s/g, '');
    const cleanedRoutingNumber = routingNumber.replace(/\s/g, '');
    savePaymentDetails({
      token: searchParams.get('token') || '',
      accountingNumber: cleanedAccountingNumber,
      routingNumber: cleanedRoutingNumber,
    })
      .then(onSuccess)
      .catch(() => {});
  };

  const isAccountNumberValid = useMemo(() => {
    const cleanedAccountingNumber = accountNumberInput.replace(/\s/g, '');
    return cleanedAccountingNumber.length >= 5 && cleanedAccountingNumber.length <= 17;
  }, [accountNumberInput]);
  const isRoutingNumberValid = useMemo(() => {
    const cleanedRoutingNumber = routingNumberInput.replace(/\s/g, '');
    return cleanedRoutingNumber.length === 9;
  }, [routingNumberInput]);
  const onHandleSubmit = (accountingNumber: string, routingNumber: string) => {
    setAccountingNumberErrorMessage('');
    setRoutingNumberErrorMessage('');
    onSubmit(accountingNumber, routingNumber);
  };
  return (
    <div className='flex flex-col items-center  h-full justify-between pb-[50px] scroll-auto'>
      <div className='flex flex-col items-center w-full'>
        <p className='font-metricRSemibold text-[18px] text-bw08 text-center px-[50px] my-[22px]'>
          {t('ASK_VENDOR.FORM.TITLE')}
        </p>
        <p className='font-metricRSemibold text-[18px] text-bw08 '>{t('ASK_VENDOR.FORM.SUBTITLE')}</p>
        <div className=' flex flex-col max-w-[327px] mt-[16px] w-full gap-2'>
          <LiliTextInput
            id='account-number-input'
            title={t('ASK_VENDOR.FORM.ACCOUNT_NUMBER_INPUT')}
            onChange={(value) => {
              setAccountNumberInput(value);
              setAccountingNumberErrorMessage('');
            }}
            value={accountNumberInput}
            errorMessage={accountingNumberErrorMessage}
            maxLength={17}
            filterType='num'
            inputMode='numeric'
            onBlur={() =>
              setAccountingNumberErrorMessage(
                isAccountNumberValid || accountNumberInput === ''
                  ? ''
                  : t('ASK_VENDOR.FORM.INLINE_ERRORS.ACCOUNT_NUMBER'),
              )}
          />
          <LiliTextInput
            id='routing-number-input'
            title={t('ASK_VENDOR.FORM.ROUTING_NUMBER_INPUT')}
            maxLength={9}
            filterType='num'
            inputMode='numeric'
            onChange={(value) => {
              setRoutingNumberInput(value);
              setRoutingNumberErrorMessage('');
            }}
            value={routingNumberInput}
            errorMessage={routingNumberErrorMessage}
            onBlur={() =>
              setRoutingNumberErrorMessage(
                isRoutingNumberValid || routingNumberInput === ''
                  ? ''
                  : t('ASK_VENDOR.FORM.INLINE_ERRORS.ROUTING_NUMBER'),
              )}
          />
        </div>
      </div>
      <div className='flex flex-col w-full px-[22px] max-w-[397px] items-center bg-red03'>
        <SecurityIcon className='text-bw07 mt-[18px]' />
        <p className='font-metricR text-[14px] text-bw07 text-center my-[20px]'>
          {t('ASK_VENDOR.FORM.DISCLAIMER', { businessName })}
        </p>
        <LiliButton
          id='confirm-button'
          title={t('ASK_VENDOR.FORM.BUTTON')}
          onClick={() => onHandleSubmit(accountNumberInput, routingNumberInput)}
          disabled={!isAccountNumberValid || !isRoutingNumberValid || isLoading}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
